import { Card, Col, Row, Space, Typography, theme } from "antd";
import { type ReactNode } from "react";
import { Link } from "react-router";

export { formatRecipeDuration, formatRecipeTotalTime } from "./formatting";

export type RecipeListCardProps = {
  title: string;
  href: string;
  image: ReactNode;
  meta: ReactNode;
  description?: string;
  tags: ReactNode[];
  tagsRowExtra?: ReactNode;
  tagsRowEnd?: ReactNode;
  detailsRow?: ReactNode;
  tagContainerTestId?: string;
  titleTestId?: string;
  rating: ReactNode;
  footerAction?: ReactNode;
  topActions?: ReactNode;
  isNew?: boolean;
  newLabel?: string;
  newBadge?: ReactNode;
  compact?: boolean;
  cardBackground?: string;
};

export function RecipeListCard({
  title,
  href,
  image,
  meta,
  description,
  tags,
  tagsRowExtra,
  tagsRowEnd,
  detailsRow,
  tagContainerTestId,
  titleTestId,
  rating,
  footerAction,
  topActions,
  isNew,
  newLabel = "NEW",
  newBadge,
  compact = false,
  cardBackground,
}: RecipeListCardProps) {
  const { token } = theme.useToken();

  return (
    <Card
      hoverable
      className="shadowed-card"
      style={{
        position: "relative",
        background: cardBackground ?? token.colorBgContainer,
        borderRadius: 18,
        width: "100%",
        height: "100%",
      }}
      styles={{
        body: {
          padding: compact ? 10 : 14,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          gap: 8,
        },
      }}
    >
      {isNew ? (newBadge ?? (
        <div
          style={{
            position: "absolute",
            top: 0,
            right: 0,
            borderTopRightRadius: 18,
            borderBottomLeftRadius: 18,
            padding: "6px 12px",
            background: token.colorPrimary,
            color: token.colorWhite,
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: 0.3,
          }}
        >
          {newLabel}
        </div>
      )) : null}
      {topActions ? <div>{topActions}</div> : null}
      <Row align="top" gutter={[compact ? 12 : 18, 8]} wrap={false} style={{ minHeight: 0 }}>
        <Col flex={compact ? "120px" : "172px"}>
          <Link to={href} style={{ display: "block", textDecoration: "none" }}>
            {image}
          </Link>
        </Col>
        <Col flex="auto" style={{ minWidth: 0 }}>
          <Space orientation="vertical" size={5} style={{ width: "100%" }}>
            <Link to={href} style={{ textDecoration: "none", color: token.colorTextBase }}>
              <Typography.Title
                level={4}
                data-testid={titleTestId}
                style={{ margin: 0, fontSize: compact ? 16 : 20, lineHeight: 1.25 }}
                ellipsis={{ rows: 2 }}
              >
                {title}
              </Typography.Title>
            </Link>
            <div style={{ color: token.colorTextSecondary }}>{meta}</div>
            {tags.length > 0 || tagsRowExtra || tagsRowEnd ? (
              <div
                style={{ width: "100%", display: "flex", alignItems: "center", gap: 8 }}
                data-testid={tagContainerTestId}
              >
                <Space wrap size={[3, 3]}>
                  {tags}
                </Space>
                {tagsRowExtra ? <div>{tagsRowExtra}</div> : null}
                {tagsRowEnd ? <div style={{ marginLeft: "auto" }}>{tagsRowEnd}</div> : null}
              </div>
            ) : null}
            {description ? (
              <Typography.Paragraph
                type="secondary"
                ellipsis={{ rows: 2 }}
                style={{ margin: 0, fontSize: 13, lineHeight: 1.45 }}
              >
                {description}
              </Typography.Paragraph>
            ) : null}
          </Space>
        </Col>
      </Row>
      {detailsRow ? <div style={{ width: "100%", marginBottom: -4 }}>{detailsRow}</div> : null}
      <div
        style={{
          marginTop: "auto",
          display: "flex",
          alignItems: "center",
          justifyContent: footerAction ? "space-between" : "flex-start",
          minWidth: 0,
        }}
      >
        {rating}
        {footerAction}
      </div>
    </Card>
  );
}
